import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useFormikContext } from "formik";
import XGSFormInput from "../../../../ui-components/form/input/xgsFormInput";
import XGSFormPhoneInput from "../../../../ui-components/form/phoneInput/xgsFormPhoneInput";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import { BolAddressBlock, BolDocument } from "../../../../app/data/pickup-requests/bolModels";
import { getFreightClasses, getPickupDays } from "../../../../slices/common/commonSlice";
import { BolReviewFormValues } from "../utils/bolReviewMappers";
import BolFieldWithAi from "./bolFieldWithAi";

const NEW_SHIPPER_ACCOUNT = 69;

type PartyBlock = "pickupFrom" | "shipTo";

export interface BolPartyFieldsProps {
  bolDoc: BolDocument;
}

const BolPartyColumn: React.FC<{
  title: string;
  block: PartyBlock;
  data?: BolAddressBlock;
  shipper?: boolean;
}> = ({ title, block, data, shipper }) => {
  const prefix = block;
  const phoneRequired = shipper;
  const { values, setFieldValue, handleBlur } = useFormikContext<BolReviewFormValues>();
  const phoneValue = values[prefix].phoneNumber;

  return (
    <div className="xgs-captured-bol__party-column">
      <h4 className="xgs-captured-bol__party-heading">{title}</h4>
      <BolFieldWithAi label="Company Name" field={data?.name} required>
        <XGSFormInput
          type="text"
          name={`${prefix}.name`}
          label=""
          labelMode={LabelModes.column}
          required
          requiredAsteriskDisabled
        />
      </BolFieldWithAi>
      <BolFieldWithAi label="Address" field={data?.addressLine_1} required>
        <XGSFormInput
          type="text"
          name={`${prefix}.addressLine_1`}
          label=""
          labelMode={LabelModes.column}
          required
          requiredAsteriskDisabled
          placeholder="Street Address"
        />
      </BolFieldWithAi>
      <BolFieldWithAi label="" field={data?.addressLine_2} showAiBadge={false}>
        <XGSFormInput
          type="text"
          name={`${prefix}.addressLine_2`}
          label=""
          labelMode={LabelModes.column}
          placeholder="Suite, Unit, Building (Optional)"
        />
      </BolFieldWithAi>
      <div className="xgs-captured-bol__party-row xgs-captured-bol__party-row--3">
        <BolFieldWithAi label="City" field={data?.city} required>
          <XGSFormInput type="text" name={`${prefix}.city`} label="" labelMode={LabelModes.column} required requiredAsteriskDisabled />
        </BolFieldWithAi>
        <BolFieldWithAi label="State" field={data?.state} required>
          <XGSFormInput type="text" name={`${prefix}.state`} label="" labelMode={LabelModes.column} required requiredAsteriskDisabled placeholder="CA" />
        </BolFieldWithAi>
        <BolFieldWithAi label="ZIP" field={data?.zip} required>
          <XGSFormInput type="text" name={`${prefix}.zip`} label="" labelMode={LabelModes.column} required requiredAsteriskDisabled placeholder="ZIP" />
        </BolFieldWithAi>
      </div>
      <BolFieldWithAi label="Phone" field={data?.phoneNumber} required={phoneRequired}>
        <XGSFormPhoneInput
          name={`${prefix}.phoneNumber`}
          label=""
          labelMode={LabelModes.column}
          required={phoneRequired}
          requiredAsteriskDisabled
          value={phoneValue}
          onValueChange={(value) => setFieldValue(`${prefix}.phoneNumber`, value)}
          onBlur={handleBlur}
        />
      </BolFieldWithAi>
      <BolFieldWithAi label="Email" field={data?.email} required={shipper}>
        <XGSFormInput
          type="text"
          name={`${prefix}.email`}
          label=""
          labelMode={LabelModes.column}
          required={shipper}
          requiredAsteriskDisabled
          placeholder="email@example.com"
        />
      </BolFieldWithAi>
    </div>
  );
};

const BolPartyFields: React.FC<BolPartyFieldsProps> = ({ bolDoc }) => {
  const dispatch = useDispatch();
  const { values } = useFormikContext<BolReviewFormValues>();
  const zip = values.pickupFrom.zip?.trim();

  useEffect(() => {
    if (zip?.length === 5) {
      dispatch(getPickupDays(NEW_SHIPPER_ACCOUNT, zip));
      dispatch(getFreightClasses(zip));
    }
  }, [dispatch, zip]);

  return (
    <div className="xgs-captured-bol__parties">
      <BolPartyColumn title="Shipper (From)" block="pickupFrom" data={bolDoc.pickupFrom} shipper />
      <BolPartyColumn title="Consignee (To)" block="shipTo" data={bolDoc.shipTo} />
    </div>
  );
};

export default BolPartyFields;
